import React, { useEffect, useRef, useState } from "react";
import AppShell from "@/components/app/AppShell";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { UserCircle, KeyRound, Camera, Trash2 } from "lucide-react";
import Avatar from "@/components/app/Avatar";

export default function ProfilePage() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "" });
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);
  const [pwErr, setPwErr] = useState("");
  const [avatarBusy, setAvatarBusy] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (user && user !== false) {
      setForm({
        first_name: user.first_name || user.name?.split(" ")[0] || "",
        last_name: user.last_name || user.name?.split(" ").slice(1).join(" ") || "",
        phone: user.phone || "",
      });
    }
  }, [user]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/auth/profile", form);
      await refresh();
      toast.success("Profile updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const changePw = async (e) => {
    e.preventDefault(); setPwErr("");
    if (pw.next.length < 8) return setPwErr("Password must be at least 8 characters.");
    if (pw.next !== pw.confirm) return setPwErr("Passwords do not match.");
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Password updated");
      setPw({ current: "", next: "", confirm: "" });
    } catch (e2) { setPwErr(formatApiError(e2.response?.data?.detail) || e2.message); }
    finally { setPwBusy(false); }
  };

  const uploadAvatar = async (file) => {
    if (!file) return;
    if (!/^image\/(jpeg|png|webp|gif)$/i.test(file.type)) {
      toast.error("Please pick a JPG, PNG, WebP, or GIF image.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB.");
      return;
    }
    setAvatarBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/avatars/${user.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Photo updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed"); }
    finally {
      setAvatarBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeAvatar = async () => {
    setAvatarBusy(true);
    try {
      await api.delete(`/avatars/${user.id}`);
      await refresh();
      toast.success("Photo removed");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Remove failed"); }
    finally { setAvatarBusy(false); }
  };

  return (
    <AppShell>
      <div className="cq-in max-w-2xl">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600">Your account</p>
        <h1 className="font-display text-4xl font-black text-slate-900">Profile</h1>
        <p className="text-slate-500 mt-1">Update your info & password.</p>

        <Card className="p-6 rounded-2xl border-slate-100 mt-6 flex flex-col sm:flex-row items-start sm:items-center gap-5" data-testid="profile-avatar-card">
          <div className="relative group">
            <Avatar
              src={user?.avatar_url}
              name={user?.name}
              size="xl"
              tone={user?.role === "client" ? "sky" : "amber"}
              ringed
              testid="profile-avatar"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={avatarBusy}
              className="absolute -bottom-1 -right-1 h-9 w-9 rounded-full bg-slate-900 text-white flex items-center justify-center shadow-lg hover:bg-amber-500 transition disabled:opacity-60"
              aria-label="Upload photo"
              data-testid="profile-avatar-upload-btn"
            >
              <Camera size={16} />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
              onChange={(e) => uploadAvatar(e.target.files?.[0])}
              data-testid="profile-avatar-file-input"
            />
          </div>

          <div className="min-w-0 flex-1">
            <p className="font-display font-black text-2xl text-slate-900 leading-tight">{user?.name}</p>
            {user?.title && (
              <p className="text-sm text-amber-600 font-bold mt-0.5">{user.title}</p>
            )}
            <p className="text-xs uppercase tracking-widest font-bold text-slate-400 mt-1">{user?.role?.replace(/_/g, " ")}</p>
            <p className="text-sm text-slate-500 mt-2 truncate">{user?.email}</p>

            <div className="flex items-center gap-2 mt-4 flex-wrap">
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={avatarBusy}
                size="sm"
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="profile-change-photo-btn"
              >
                <Camera size={14} className="mr-1.5" />
                {avatarBusy ? "Uploading…" : user?.avatar_url ? "Change photo" : "Upload photo"}
              </Button>
              {user?.avatar_url && (
                <Button
                  onClick={removeAvatar}
                  disabled={avatarBusy}
                  size="sm"
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                  data-testid="profile-remove-photo-btn"
                >
                  <Trash2 size={14} className="mr-1.5" /> Remove
                </Button>
              )}
              <span className="text-[11px] text-slate-400 font-medium">JPG, PNG, WEBP or GIF · under 5 MB</span>
            </div>
          </div>
        </Card>

        <Card className="p-6 rounded-2xl border-slate-100 mt-6" data-testid="profile-card">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center"><UserCircle size={18} /></div>
            <h3 className="font-display font-bold text-lg">Personal info</h3>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>First name</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="profile-first-name" /></div>
            <div><Label>Last name</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="profile-last-name" /></div>
            <div className="col-span-2"><Label>Email</Label><Input value={user?.email || ""} disabled data-testid="profile-email-disabled" /></div>
            <div className="col-span-2"><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="profile-phone" /></div>
          </div>
          <Button onClick={save} disabled={busy} className="mt-4 bg-amber-500 hover:bg-amber-600" data-testid="profile-save">{busy ? "Saving…" : "Save profile"}</Button>
        </Card>

        <Card className="p-6 rounded-2xl border-slate-100 mt-6" data-testid="password-card">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center"><KeyRound size={18} /></div>
            <h3 className="font-display font-bold text-lg">Change password</h3>
          </div>
          <form onSubmit={changePw} className="space-y-3">
            <div><Label>Current password</Label><Input type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} required data-testid="pw-current" /></div>
            <div><Label>New password</Label><Input type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} required data-testid="pw-new" /></div>
            <div><Label>Confirm new password</Label><Input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} required data-testid="pw-confirm" /></div>
            {pwErr && <p className="text-sm text-red-600" data-testid="pw-error">{pwErr}</p>}
            <Button type="submit" disabled={pwBusy} className="bg-amber-500 hover:bg-amber-600" data-testid="pw-submit">{pwBusy ? "Updating…" : "Update password"}</Button>
          </form>
        </Card>
      </div>
    </AppShell>
  );
}
